import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  FastForward,
  Rewind,
  Share2,
  Bookmark,
  FileText,
  Volume2,
  VolumeX,
  Captions,
  Radio,
  Users,
  Star,
  Clock,
  MapPin,
  ChevronUp,
} from 'lucide-react';

import ChapterTimeline from './ChapterTimeline';
import { formatDurationString, shakaStyles } from '../utils/audioUtils';

const AudioPlayer = ({
  currentItem,
  isRadioMode,
  radioState,
  onNext,
  onManualEnded,
  shouldAutoPlay,
  history,
  saveHistory,
  toggleFavorite,
  isFavorite,
  onStartParty,
  isMini = false,
  onExpand,
  partySession,
  savedBookmarks = [],
  onAddBookmark,
  onRemoveBookmark,
  radioMuted,
  setRadioMuted,
  onExitRadio,
  showToast,
  onClick,
  videoElement,
  playerInstance,
  playerError,
  isSubtitlesOn,
  setIsSubtitlesOn,
  partyCountdown,
  onPlaybackChange,
}) => {
  const containerRef = useRef(null);
  const componentRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(100);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [countdownVal, setCountdownVal] = useState(10);

  const isCountdown = currentItem?.type === 'countdown';
  const lastSavedTime = useRef(0);
  const lastSavedId = useRef(null);

  // Scroll to player when not mini
  useEffect(() => {
    if (!isMini && componentRef.current) {
      const timer = setTimeout(() => {
        const element = componentRef.current;
        if (element) {
          const headerOffset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.scrollY - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMini, currentItem?.id]);

  const isVisible =
    !isPlaying || isHovered || showControls || isCountdown || !!partyCountdown;

  const togglePlay = useCallback(
    (e) => {
      if (e) e.stopPropagation();

      if (isRadioMode) {
        const newMutedState = !radioMuted;
        if (setRadioMuted) setRadioMuted(newMutedState);

        if (videoElement) {
          if (!newMutedState) {
            videoElement.muted = false;
            videoElement.play().catch((err) => {
              console.warn('Manual play failed', err);
            });
          } else {
            videoElement.pause();
          }
        }
      } else if (!isCountdown && videoElement) {
        if (videoElement.paused) {
          videoElement.muted = false;
          videoElement.play().catch((err) => {
            if (err.name !== 'AbortError')
              console.warn('Manual play failed', err);
          });
        } else {
          videoElement.pause();
        }
      }
    },
    [isRadioMode, isCountdown, radioMuted, setRadioMuted, videoElement]
  );

  const seek = (time) => {
    if (!isRadioMode && !isCountdown && videoElement)
      videoElement.currentTime = time;
  };
  const skip = (sec) =>
    seek(Math.min(Math.max(currentTime + sec, 0), duration));
  const changeSpeed = () => {
    const newRate =
      playbackRate === 1
        ? 1.25
        : playbackRate === 1.25
          ? 1.5
          : playbackRate === 1.5
            ? 2
            : 1;
    setPlaybackRate(newRate);
    if (videoElement) videoElement.playbackRate = newRate;
  };
  const toggleSubtitles = () => {
    if (playerInstance) {
      const newState = !isSubtitlesOn;
      setIsSubtitlesOn(newState);
      playerInstance.setTextTrackVisibility(newState);
    }
  };
  const copyShareLink = () => {
    if (!currentItem) return;
    const baseUrl = window.isNativeApp
      ? 'https://baws.in'
      : window.location.origin;
    const url = `${baseUrl}/listen?item=${encodeURIComponent(currentItem.id)}`;
    navigator.clipboard.writeText(url).then(() => {
      if (showToast) showToast(`Link copied: ${url}`);
    });
  };

  const skipCountdown = () => {
    if (!isRadioMode && isCountdown && onNext) onNext();
  };
  const nextChapter = () => {
    if (!currentItem) return;
    const idx =
      currentItem.chapters?.findIndex(
        (c) => currentTime >= c.start && currentTime < c.end
      ) ?? -1;
    if (idx < (currentItem.chapters?.length || 0) - 1)
      seek(currentItem.chapters[idx + 1].start);
  };
  const prevChapter = () => {
    if (!currentItem) return;
    const idx =
      currentItem.chapters?.findIndex(
        (c) => currentTime >= c.start && currentTime < c.end
      ) ?? -1;
    if (idx > 0) seek(currentItem.chapters[idx - 1].start);
    else seek(0);
  };

  const toggleOverlay = useCallback(() => {
    setShowControls((prev) => !prev);
  }, []);

  const stopProp = (e) => e.stopPropagation();

  // Sync local state with global video element
  useEffect(() => {
    if (!videoElement) return;

    const updateTime = () => {
      const vTime = videoElement.currentTime;
      const vDur = videoElement.duration || 100;
      const vPaused = videoElement.paused;

      setCurrentTime((prev) => (Math.abs(prev - vTime) > 0.1 ? vTime : prev));
      setDuration((prev) => (Math.abs(prev - vDur) > 0.1 ? vDur : prev));
      setIsPlaying(!vPaused);

      // Save history logic
      if (!isRadioMode && !isCountdown && vTime > 0 && currentItem?.id) {
        const now = Date.now();
        if (
          now - lastSavedTime.current > 5000 ||
          lastSavedId.current !== currentItem.id
        ) {
          saveHistory(currentItem.id, vTime);
          lastSavedTime.current = now;
          lastSavedId.current = currentItem.id;
        }
      }
    };

    const handlePlayPause = () => {
      const playing = !videoElement.paused;
      setIsPlaying(playing);
      if (onPlaybackChange) onPlaybackChange(playing);
    };

    const handleEnded = () => {
      const timeoutId = setTimeout(() => {
        if (!isRadioMode && onManualEnded) onManualEnded();
        else if (onNext) onNext();
      }, 1000);
      return () => clearTimeout(timeoutId);
    };

    videoElement.addEventListener('timeupdate', updateTime);
    videoElement.addEventListener('play', handlePlayPause);
    videoElement.addEventListener('pause', handlePlayPause);
    videoElement.addEventListener('ended', handleEnded);

    return () => {
      videoElement.removeEventListener('timeupdate', updateTime);
      videoElement.removeEventListener('play', handlePlayPause);
      videoElement.removeEventListener('pause', handlePlayPause);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [
    videoElement,
    isCountdown,
    isRadioMode,
    onManualEnded,
    onNext,
    currentItem?.id,
    saveHistory,
    onPlaybackChange,
  ]);

  useEffect(() => {
    if (isCountdown && !partyCountdown) {
      const timer = setInterval(() => {
        setCountdownVal((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            if (!isRadioMode && onNext) setTimeout(() => onNext(), 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCountdownVal(10);
    }
  }, [isCountdown, isRadioMode, onNext, partyCountdown]);

  // Mini Player Drag Logic
  const [position, setPosition] = useState(null); // null = default bottom-center
  const [dragOutlinePosition, setDragOutlinePosition] = useState(null);
  const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const hasMoved = useRef(false);

  const getClientCoordinates = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const handlePointerDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag if clicking buttons
    if (e.type === 'mousedown') e.preventDefault();
    hasMoved.current = false;

    let startX = 0;
    let startY = 0;

    const currentRect = e.currentTarget.getBoundingClientRect();

    if (position) {
      startX = position.x;
      startY = position.y;
    } else {
      startX = currentRect.left;
      startY = currentRect.top;
      setPosition({ x: startX, y: startY });
    }

    const { clientX, clientY } = getClientCoordinates(e);

    dragRef.current = {
      startX: clientX,
      startY: clientY,
      initialX: startX,
      initialY: startY,
      width: currentRect.width,
      height: currentRect.height,
    };

    if (e.type === 'touchstart') {
      document.addEventListener('touchmove', handlePointerMove, {
        passive: false,
      });
      document.addEventListener('touchend', handlePointerUp);
    } else {
      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
    }
  };

  const handlePointerMove = useCallback((e) => {
    const { clientX, clientY } = getClientCoordinates(e);
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;

    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasMoved.current = true;
      if (e.cancelable) {
        e.preventDefault();
      }
    }

    if (hasMoved.current) {
      const newX = dragRef.current.initialX + dx;
      const newY = dragRef.current.initialY + dy;
      dragRef.current.currentX = newX;
      dragRef.current.currentY = newY;
      setDragOutlinePosition({ x: newX, y: newY });
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    document.removeEventListener('mousemove', handlePointerMove);
    document.removeEventListener('mouseup', handlePointerUp);
    document.removeEventListener('touchmove', handlePointerMove);
    document.removeEventListener('touchend', handlePointerUp);

    if (hasMoved.current && dragRef.current.currentX !== undefined) {
      // Hide if thrown to bottom
      if (dragRef.current.currentY > window.innerHeight - 50) {
        if (videoElement) {
          videoElement.pause();
        }
        if (isRadioMode && setRadioMuted) {
          setRadioMuted(true);
        }
        if (onExitRadio) {
          onExitRadio();
        }
        setPosition(null);
      } else {
        setPosition({
          x: dragRef.current.currentX,
          y: dragRef.current.currentY,
        });
      }
    }
    setDragOutlinePosition(null);
  }, [
    handlePointerMove,
    videoElement,
    isRadioMode,
    setRadioMuted,
    onExitRadio,
  ]);

  const handleMiniClick = (e) => {
    if (!hasMoved.current && onClick) {
      onClick();
    }
  };

  if (!currentItem) return null;

  if (isMini) {
    const styleProps = position
      ? { left: position.x, top: position.y, transform: 'none' }
      : {};

    return (
      <>
        {dragOutlinePosition && (
          <div
            className="fixed z-[101] pointer-events-none rounded-full border-2 border-dashed border-blue-500 bg-blue-500/10"
            style={{
              left: dragOutlinePosition.x,
              top: dragOutlinePosition.y,
              width: dragRef.current.width,
              height: dragRef.current.height,
            }}
          />
        )}
        <div
          role="button"
          tabIndex={0}
          className={`fixed z-[100] cursor-move select-none bg-slate-900/95 backdrop-blur-md border border-slate-700/50 p-2 flex items-center gap-3 shadow-2xl transition-all duration-300 ease-in-out hover:shadow-blue-900/20 ${
            position ? '' : 'bottom-20 md:bottom-6 left-1/2 -translate-x-1/2'
          } rounded-full max-w-[90vw] w-[380px]`}
          style={styleProps}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
          onClick={handleMiniClick}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && onClick) {
              onClick();
            }
          }}
        >
          <div
            className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden flex-shrink-0 bg-slate-800 relative group cursor-pointer border border-slate-700"
            onClick={(e) => {
              e.stopPropagation();
              if (onExpand) onExpand();
            }}
          >
            <img
              src={currentItem.thumbnail}
              className="w-full h-full object-cover"
              alt={currentItem.title}
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronUp size={20} className="text-white" />
            </div>
          </div>

          <div className="flex-grow min-w-0 flex flex-col justify-center">
            <h4 className="text-xs md:text-sm font-bold text-white truncate pr-2 leading-tight">
              {currentItem.title}
            </h4>
            <div className="flex items-center gap-2 text-[10px] md:text-xs text-slate-400 mt-0.5 w-full">
              <span className="font-mono tabular-nums min-w-[35px]">
                {isRadioMode ? 'LIVE' : formatDurationString(currentTime)}
              </span>
              {!isRadioMode && (
                <div className="flex-grow h-1.5 bg-slate-700 rounded-full overflow-hidden mr-2">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(
                          0,
                          ((currentTime || 0) / (duration || 1)) * 100
                        )
                      )}%`,
                    }}
                  ></div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pr-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isRadioMode) return;
                skip(-10);
              }}
              disabled={isRadioMode}
              className="p-2 text-slate-400 hover:text-white rounded-full hover:bg-white/10 hidden sm:block"
            >
              <Rewind size={18} />
            </button>

            <button
              onClick={togglePlay}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white text-black flex items-center justify-center hover:bg-slate-200 shadow-lg active:scale-95 transition-transform"
            >
              {isRadioMode ? (
                radioMuted ? (
                  <VolumeX size={20} fill="currentColor" />
                ) : (
                  <Volume2 size={20} fill="currentColor" />
                )
              ) : isPlaying ? (
                <Pause size={20} fill="currentColor" />
              ) : (
                <Play size={20} fill="currentColor" className="ml-0.5" />
              )}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isRadioMode) return;
                skip(10);
              }}
              disabled={isRadioMode}
              className="p-2 text-slate-400 hover:text-white rounded-full hover:bg-white/10 hidden sm:block"
            >
              <FastForward size={18} />
            </button>
          </div>
        </div>
      </>
    );
  }
  return (
    <div
      ref={componentRef}
      className="tour-player-main bg-slate-900 rounded-xl p-0 md:p-0 shadow-2xl border border-slate-700 flex flex-col h-full overflow-hidden"
    >
      <style>{shakaStyles}</style>
      <div
        ref={containerRef}
        className="relative w-full bg-black group cursor-pointer aspect-video flex items-center justify-center overflow-hidden"
        onClick={toggleOverlay}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="w-full h-full object-contain bg-black"></div>

        {currentItem.thumbnail && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none bg-black">
            <img
              src={currentItem.thumbnail}
              alt={currentItem.title}
              className="w-full h-full object-contain opacity-100"
            />
          </div>
        )}
        {playerError && (
          <div className="absolute inset-0 z-[80] bg-black/90 flex flex-col items-center justify-center p-8 text-center">
            <div className="mb-4 p-4 bg-red-900/50 rounded-full border border-red-500 text-red-500">
              <Radio size={32} />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">
              Playback Error
            </h3>
            <p className="text-slate-400 mb-6 max-w-md">{playerError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-white text-black font-bold rounded-full hover:bg-slate-200"
            >
              Reload Page
            </button>
          </div>
        )}

        {isRadioMode && (
          <div className="absolute top-4 right-4 flex items-center gap-2 text-red-500 animate-pulse z-20 bg-black/50 px-2 py-1 rounded backdrop-blur pointer-events-none">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-xs font-bold tracking-widest uppercase">
              Live
            </span>
          </div>
        )}
        {isRadioMode && radioMuted && !playerError && (
          <div
            className="absolute inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              if (setRadioMuted) setRadioMuted(false);
              if (videoElement) {
                videoElement.muted = false;
                videoElement.play().catch((e) => {
                  console.log(e);
                });
              }
            }}
          >
            <button className="group relative flex items-center gap-3 px-8 py-4 bg-white text-black rounded-full font-bold shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:scale-105 transition-transform">
              <div className="absolute inset-0 rounded-full border-2 border-white animate-ping opacity-20"></div>
              <VolumeX size={24} className="text-red-500" />
              <span>Tap to Unmute Radio</span>
            </button>
          </div>
        )}
        {partyCountdown && partySession && (
          <div
            className="absolute inset-0 z-50 bg-slate-900/90 backdrop-blur-md flex flex-col items-center justify-center text-center p-6 cursor-default"
            onClick={stopProp}
          >
            <div className="animate-pulse-ring rounded-full mb-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center shadow-xl">
                <Users size={40} className="text-white" />
              </div>
            </div>
            <h2 className="text-blue-400 text-sm font-bold uppercase tracking-widest mb-2">
              Listening Together Party
            </h2>
            <h1 className="text-white text-xl md:text-2xl font-bold mb-8 max-w-xl">
              {currentItem.title}
            </h1>

            <div className="grid grid-cols-2 gap-8 text-center mb-8">
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase mb-1">
                  Starting At
                </p>
                <p className="text-white font-mono text-lg">
                  {new Date(partySession.startTime).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase mb-1">
                  In
                </p>
                <p className="text-white font-mono text-lg">{partyCountdown}</p>
              </div>
            </div>
            <p className="text-slate-500 text-xs animate-pulse">
              Waiting for host to sync...
            </p>
          </div>
        )}

        {isCountdown && (
          <div
            className="absolute inset-0 z-50 bg-slate-900/95 flex flex-col items-center justify-center text-center backdrop-blur-sm p-4 cursor-default"
            onClick={stopProp}
          >
            {!isRadioMode && (
              <button
                onClick={skipCountdown}
                className="absolute top-4 right-4 flex items-center gap-1 text-slate-400 hover:text-white bg-slate-800 px-3 py-1 rounded-full text-xs font-bold"
              >
                <FastForward size={12} />
              </button>
            )}
            <h2 className="text-slate-400 text-xs uppercase tracking-widest font-bold mb-1">
              Up Next
            </h2>
            <h1 className="text-lg md:text-xl font-bold text-white mb-4 line-clamp-2 px-4">
              {currentItem.nextItemTitle}
            </h1>
            <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#1e293b"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="8"
                  strokeDasharray="280"
                  strokeDashoffset={280 - (280 * (10 - countdownVal)) / 10}
                  className="transition-all duration-1000 ease-linear"
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-4xl font-black text-white font-mono tabular-nums">
                {countdownVal}
              </span>
            </div>
          </div>
        )}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent flex flex-col justify-end p-4 transition-opacity duration-300 z-[60] ${isVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        >
          <div className="absolute top-4 right-4 flex gap-2" onClick={stopProp}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                changeSpeed();
              }}
              disabled={isRadioMode || isCountdown || partyCountdown}
              className="tour-speed-control px-2 py-1 bg-black/50 hover:bg-black/70 rounded text-xs font-bold text-white border border-white/20 backdrop-blur-md"
            >
              {playbackRate}x
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSubtitles();
              }}
              disabled={isCountdown}
              className={`p-1.5 rounded bg-black/50 hover:bg-black/70 border border-white/20 backdrop-blur-md ${isSubtitlesOn ? 'text-blue-400' : 'text-white'}`}
            >
              <Captions size={16} />
            </button>
          </div>
          <div className="w-full" onClick={stopProp}>
            <div className="flex items-center justify-between">
              <div className="tour-play-controls flex items-center gap-4">
                <button
                  onClick={prevChapter}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="hidden md:flex text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <SkipBack size={20} />
                </button>
                <button
                  onClick={() => skip(-10)}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <Rewind size={24} />
                </button>

                <button
                  onClick={togglePlay}
                  disabled={(isRadioMode && isCountdown) || partyCountdown}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-black hover:bg-slate-200 transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
                >
                  {isRadioMode ? (
                    radioMuted ? (
                      <VolumeX size={24} fill="currentColor" />
                    ) : (
                      <Volume2 size={24} fill="currentColor" />
                    )
                  ) : isPlaying && !isCountdown && !partyCountdown ? (
                    <Pause size={24} fill="currentColor" />
                  ) : (
                    <Play size={24} fill="currentColor" className="ml-0.5" />
                  )}
                </button>

                <button
                  onClick={() => skip(10)}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <FastForward size={24} />
                </button>
                <button
                  onClick={nextChapter}
                  disabled={isRadioMode || isCountdown || partyCountdown}
                  className="hidden md:flex text-slate-300 hover:text-white disabled:opacity-30"
                >
                  <SkipForward size={20} />
                </button>
              </div>
              <div className="tour-interactive-tools flex items-center gap-4">
                <button
                  onClick={() => {
                    const bookmarkTime = Math.max(0, currentTime - 20);
                    onAddBookmark(currentItem.id, bookmarkTime);
                  }}
                  className={`${savedBookmarks.length > 0 ? 'text-yellow-400' : 'text-slate-300'} hover:text-yellow-300 disabled:opacity-30`}
                  disabled={isCountdown || partyCountdown}
                  title="Add Bookmark (T-20s) & Copy Link"
                >
                  <Bookmark
                    size={20}
                    fill={savedBookmarks.length > 0 ? 'currentColor' : 'none'}
                  />
                </button>
                <button
                  onClick={copyShareLink}
                  className="text-slate-300 hover:text-blue-400"
                >
                  <Share2 size={20} />
                </button>
                <button
                  onClick={() => toggleFavorite(currentItem.id)}
                  className={`${isFavorite ? 'text-yellow-400' : 'text-slate-300'} hover:text-yellow-300`}
                >
                  <Star size={20} fill={isFavorite ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={() => onStartParty(currentItem)}
                  className="text-slate-300 hover:text-green-400"
                >
                  <Users size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 md:px-6 md:pt-6 bg-slate-900">
        <div className="mb-2 group/timeline relative">
          <div className="tour-timeline">
            <ChapterTimeline
              chapters={currentItem.chapters}
              duration={duration}
              currentTime={currentTime}
              onSeek={seek}
            />
            <div className="relative h-3 flex items-center">
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={currentTime}
                onChange={(e) => seek(Number(e.target.value))}
                disabled={isRadioMode || isCountdown || partyCountdown}
                className={`absolute w-full h-1 bg-white/30 rounded-lg appearance-none cursor-pointer accent-blue-500 hover:h-1.5 transition-all z-20 ${isRadioMode || isCountdown || partyCountdown ? 'cursor-not-allowed opacity-50' : ''}`}
              />
              <div
                className="absolute h-1 bg-blue-500 rounded-lg z-10 pointer-events-none"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />

              {savedBookmarks &&
                savedBookmarks.map((bmTime, index) => (
                  <div
                    key={index}
                    className="absolute w-3 h-3 bg-yellow-400 border border-black rounded-full z-30 cursor-pointer hover:scale-150 transition-transform shadow-lg"
                    style={{
                      left: `${(bmTime / (duration || 1)) * 100}%`,
                      marginLeft: '-6px',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      seek(bmTime);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      onRemoveBookmark(currentItem.id, bmTime);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveBookmark(currentItem.id, bmTime);
                    }}
                    title={`Jump to bookmark: ${formatDurationString(bmTime)} (Double-click or Long-press to remove)`}
                  />
                ))}
            </div>
          </div>
          <div className="flex justify-between text-xs font-medium text-slate-300 mt-1 tabular-nums">
            <span>{formatDurationString(currentTime)}</span>
            <span>{formatDurationString(duration)}</span>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 bg-slate-900 border-t border-slate-800 pt-2 md:pt-2">
        <h1 className="text-lg md:text-xl font-bold text-slate-100 leading-snug mb-2 line-clamp-2">
          {currentItem.title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-slate-400">
          {!isCountdown && (
            <span className="bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
              #{currentItem.number}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock size={14} /> {currentItem.date}
          </span>
          <span className="flex items-center gap-1">
            <MapPin size={14} /> {currentItem.place}
          </span>
          {currentItem.dest && (
            <a
              href={currentItem.dest}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline"
            >
              <FileText size={14} /> Source Text
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
